/** Shared, data-only web palette catalogue. Core and both web skins consume identical IDs/colours. */
export type ThemeMode = "light" | "dark";
export interface ThemePalette {
  bgPrimary: string;
  bgSecondary: string;
  bgHover?: string;
  textPrimary: string;
  textSecondary: string;
  borderColor: string;
  accent: string;
  accentHover?: string;
  danger: string;
  success: string;
  warning?: string;
  /** Authored code roles are separate from UI text/contrast adjustments. */
  codeBackground?: string;
  codeForeground?: string;
  syntax?: Record<string, string>;
  /** Authored ANSI roles; palette generation still enforces readable foregrounds. */
  terminal?: Record<string, string>;
  /** Preserve a single phosphor hue in contrast fallbacks and interaction overlays. */
  monochrome?: boolean;
}
export interface ThemePreset {
  id: string;
  label: string;
  mode: ThemeMode | "auto";
  light?: ThemePalette;
  dark?: ThemePalette;
  glow?: boolean;
  source?: string;
}
export const WEB_THEME_PRESETS: ThemePreset[] = [
  {
    id: "default",
    label: "Default",
    mode: "auto",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f7f9fa",
      bgHover: "#e8ebed",
      textPrimary: "#0f1419",
      textSecondary: "#536471",
      borderColor: "#eff3f4",
      accent: "#1d9bf0",
      accentHover: "#1a8cd8",
      warning: "#f0b429",
      danger: "#f4212e",
      success: "#00ba7c",
      codeForeground: "#0f1419",
      codeBackground: "#ffffff",
      syntax: {
        keyword: "#1d9bf0",
        operator: "#0f1419",
        number: "#f0b429",
        string: "#00ba7c",
        regexp: "#00ba7c",
        comment: "#536471",
        variable: "#0f1419",
        variable2: "#0f1419",
        definition: "#0f1419",
        function: "#1d9bf0",
        local: "#0f1419",
        property: "#1d9bf0",
        propertyDefinition: "#1d9bf0",
        type: "#0f1419",
        class: "#0f1419",
        namespace: "#0f1419",
        label: "#0f1419",
        macro: "#1d9bf0",
        atom: "#f0b429",
        bool: "#f0b429",
        punctuation: "#0f1419",
        meta: "#1d9bf0",
        link: "#00ba7c",
        heading: "#1d9bf0",
        invalid: "#f4212e",
        deleted: "#f4212e",
        inserted: "#00ba7c",
      },
    },
    dark: {
      bgPrimary: "#000000",
      bgSecondary: "#16181c",
      bgHover: "#1d1f23",
      textPrimary: "#e7e9ea",
      textSecondary: "#71767b",
      borderColor: "#2f3336",
      accent: "#1d9bf0",
      accentHover: "#1a8cd8",
      warning: "#f0b429",
      danger: "#f4212e",
      success: "#00ba7c",
      syntax: {
        keyword: "#1d9bf0",
        operator: "#e7e9ea",
        number: "#1d9bf0",
        string: "#1d9bf0",
        regexp: "#1d9bf0",
        comment: "#71767b",
        variable: "#e7e9ea",
        variable2: "#e7e9ea",
        definition: "#e7e9ea",
        function: "#1d9bf0",
        local: "#e7e9ea",
        property: "#1d9bf0",
        propertyDefinition: "#1d9bf0",
        type: "#1d9bf0",
        class: "#1d9bf0",
        namespace: "#1d9bf0",
        label: "#e7e9ea",
        macro: "#1d9bf0",
        atom: "#1d9bf0",
        bool: "#1d9bf0",
        punctuation: "#e7e9ea",
        meta: "#1d9bf0",
        link: "#1d9bf0",
        heading: "#1d9bf0",
        invalid: "#f4212e",
        deleted: "#f4212e",
        inserted: "#00ba7c",
      },
      codeForeground: "#e7e9ea",
      codeBackground: "#000000",
    },
  },
  {
    id: "tango",
    label: "Tango",
    mode: "light",
    light: {
      bgPrimary: "#f6f5f4",
      bgSecondary: "#efedeb",
      bgHover: "#e5e3e1",
      textPrimary: "#2e3436",
      textSecondary: "#5c6466",
      borderColor: "#d3d7cf",
      accent: "#3465a4",
      accentHover: "#2c5890",
      danger: "#cc0000",
      success: "#4e9a06",
      codeForeground: "#2e3436",
      codeBackground: "#f6f5f4",
      syntax: {
        keyword: "#3465a4",
        operator: "#2e3436",
        number: "#3465a4",
        string: "#4e9a06",
        regexp: "#4e9a06",
        comment: "#5c6466",
        variable: "#2e3436",
        variable2: "#2e3436",
        definition: "#2e3436",
        function: "#3465a4",
        local: "#2e3436",
        property: "#3465a4",
        propertyDefinition: "#3465a4",
        type: "#2e3436",
        class: "#2e3436",
        namespace: "#2e3436",
        label: "#2e3436",
        macro: "#3465a4",
        atom: "#3465a4",
        bool: "#3465a4",
        punctuation: "#2e3436",
        meta: "#3465a4",
        link: "#4e9a06",
        heading: "#3465a4",
        invalid: "#cc0000",
        deleted: "#cc0000",
        inserted: "#4e9a06",
      },
    },
  },
  {
    id: "xterm",
    label: "XTerm",
    mode: "dark",
    dark: {
      bgPrimary: "#000000",
      bgSecondary: "#0a0a0a",
      bgHover: "#121212",
      textPrimary: "#d0d0d0",
      textSecondary: "#8a8a8a",
      borderColor: "#1f1f1f",
      accent: "#00a2ff",
      accentHover: "#0086d1",
      danger: "#ff5f5f",
      success: "#5fff87",
      codeForeground: "#d0d0d0",
      codeBackground: "#000000",
      syntax: {
        keyword: "#00a2ff",
        operator: "#d0d0d0",
        number: "#00a2ff",
        string: "#5fff87",
        regexp: "#5fff87",
        comment: "#8a8a8a",
        variable: "#d0d0d0",
        variable2: "#d0d0d0",
        definition: "#d0d0d0",
        function: "#00a2ff",
        local: "#d0d0d0",
        property: "#00a2ff",
        propertyDefinition: "#00a2ff",
        type: "#d0d0d0",
        class: "#d0d0d0",
        namespace: "#d0d0d0",
        label: "#d0d0d0",
        macro: "#00a2ff",
        atom: "#00a2ff",
        bool: "#00a2ff",
        punctuation: "#d0d0d0",
        meta: "#00a2ff",
        link: "#5fff87",
        heading: "#00a2ff",
        invalid: "#ff5f5f",
        deleted: "#ff5f5f",
        inserted: "#5fff87",
      },
    },
  },
  {
    id: "monokai",
    label: "Monokai Original",
    mode: "dark",
    dark: {
      bgPrimary: "#272822",
      bgSecondary: "#2f2f2f",
      bgHover: "#2f2f2f",
      textPrimary: "#f8f8f2",
      textSecondary: "#cfcfc2",
      borderColor: "#3e3d32",
      accent: "#f92672",
      accentHover: "#f92672",
      danger: "#f92672",
      success: "#a6e22e",
      warning: "#e6db74",
      syntax: {
        keyword: "#f92672",
        operator: "#f8f8f2",
        number: "#ae81ff",
        string: "#e6db74",
        regexp: "#e6db74",
        comment: "#cfcfc2",
        variable: "#f8f8f2",
        variable2: "#f8f8f2",
        definition: "#f8f8f2",
        function: "#f92672",
        local: "#f8f8f2",
        property: "#f92672",
        propertyDefinition: "#f92672",
        type: "#f8f8f2",
        class: "#f8f8f2",
        namespace: "#f8f8f2",
        label: "#f8f8f2",
        macro: "#f92672",
        atom: "#ae81ff",
        bool: "#ae81ff",
        punctuation: "#f8f8f2",
        meta: "#f92672",
        link: "#e6db74",
        heading: "#f92672",
        invalid: "#f92672",
        deleted: "#f92672",
        inserted: "#a6e22e",
      },
      codeForeground: "#f8f8f2",
      codeBackground: "#272822",
    },
    source: "https://monokai.pro/",
  },
  {
    id: "monokai-pro",
    label: "Monokai Pro",
    mode: "dark",
    dark: {
      bgPrimary: "#2d2a2e",
      bgSecondary: "#363237",
      bgHover: "#363237",
      textPrimary: "#fcfcfa",
      textSecondary: "#c1c0c0",
      borderColor: "#444046",
      accent: "#ff6188",
      accentHover: "#ff6188",
      danger: "#ff4f5e",
      success: "#a9dc76",
      warning: "#ffd866",
      syntax: {
        keyword: "#ff6188",
        operator: "#ff6188",
        number: "#ab9df2",
        string: "#ffd866",
        regexp: "#ffd866",
        comment: "#727072",
        variable: "#fcfcfa",
        variable2: "#fcfcfa",
        definition: "#fcfcfa",
        function: "#a9dc76",
        local: "#fcfcfa",
        property: "#78dce8",
        propertyDefinition: "#78dce8",
        type: "#78dce8",
        class: "#78dce8",
        namespace: "#78dce8",
        label: "#fcfcfa",
        macro: "#a9dc76",
        atom: "#ab9df2",
        bool: "#ab9df2",
        punctuation: "#fcfcfa",
        meta: "#ff6188",
        link: "#ffd866",
        heading: "#ff6188",
        invalid: "#fcfcfa",
        deleted: "#fcfcfa",
        inserted: "#fcfcfa",
      },
      codeForeground: "#fcfcfa",
      codeBackground: "#2d2a2e",
    },
    source: "https://monokai.pro/",
  },
  {
    id: "ristretto",
    label: "Ristretto",
    mode: "dark",
    dark: {
      bgPrimary: "#2c2525",
      bgSecondary: "#362d2d",
      bgHover: "#403535",
      textPrimary: "#f4f1ef",
      textSecondary: "#cbbdb8",
      borderColor: "#4a3c3c",
      accent: "#ff9f43",
      accentHover: "#f28a2e",
      danger: "#ff5f56",
      success: "#a9dc76",
      codeForeground: "#f4f1ef",
      codeBackground: "#2c2525",
      syntax: {
        keyword: "#ff9f43",
        operator: "#f4f1ef",
        number: "#ff9f43",
        string: "#a9dc76",
        regexp: "#a9dc76",
        comment: "#cbbdb8",
        variable: "#f4f1ef",
        variable2: "#f4f1ef",
        definition: "#f4f1ef",
        function: "#ff9f43",
        local: "#f4f1ef",
        property: "#ff9f43",
        propertyDefinition: "#ff9f43",
        type: "#f4f1ef",
        class: "#f4f1ef",
        namespace: "#f4f1ef",
        label: "#f4f1ef",
        macro: "#ff9f43",
        atom: "#ff9f43",
        bool: "#ff9f43",
        punctuation: "#f4f1ef",
        meta: "#ff9f43",
        link: "#a9dc76",
        heading: "#ff9f43",
        invalid: "#ff5f56",
        deleted: "#ff5f56",
        inserted: "#a9dc76",
      },
    },
  },
  {
    id: "dracula",
    label: "Dracula",
    mode: "dark",
    dark: {
      bgPrimary: "#282a36",
      bgSecondary: "#303445",
      bgHover: "#3a3f52",
      textPrimary: "#f8f8f2",
      textSecondary: "#c5c8d6",
      borderColor: "#44475a",
      accent: "#bd93f9",
      accentHover: "#a87ded",
      danger: "#ff5555",
      success: "#50fa7b",
      syntax: {
        keyword: "#ff79c6",
        operator: "#ff79c6",
        number: "#bd93f9",
        string: "#f1fa8c",
        regexp: "#f1fa8c",
        comment: "#6272a4",
        variable: "#f8f8f2",
        variable2: "#f8f8f2",
        definition: "#f8f8f2",
        function: "#50fa7b",
        local: "#f8f8f2",
        property: "#8be9fd",
        propertyDefinition: "#8be9fd",
        type: "#8be9fd",
        class: "#8be9fd",
        namespace: "#8be9fd",
        label: "#f8f8f2",
        macro: "#50fa7b",
        atom: "#bd93f9",
        bool: "#bd93f9",
        punctuation: "#f8f8f2",
        meta: "#ff79c6",
        link: "#f1fa8c",
        heading: "#ff79c6",
        invalid: "#f8f8f2",
        deleted: "#f8f8f2",
        inserted: "#f8f8f2",
      },
      codeForeground: "#f8f8f2",
      codeBackground: "#282a36",
    },
  },
  {
    id: "catppuccin",
    label: "Catppuccin Mocha",
    mode: "dark",
    dark: {
      bgPrimary: "#1e1e2e",
      bgSecondary: "#181825",
      bgHover: "#313244",
      textPrimary: "#cdd6f4",
      textSecondary: "#a6adc8",
      borderColor: "#585b70",
      accent: "#89b4fa",
      accentHover: "#89b4fa",
      danger: "#f38ba8",
      success: "#a6e3a1",
      warning: "#f9e2af",
      syntax: {
        keyword: "#cba6f7",
        operator: "#89dceb",
        number: "#fab387",
        string: "#a6e3a1",
        regexp: "#f5c2e7",
        comment: "#9399b2",
        variable: "#cdd6f4",
        variable2: "#f38ba8",
        definition: "#cdd6f4",
        function: "#89b4fa",
        local: "#eba0ac",
        property: "#89b4fa",
        propertyDefinition: "#89b4fa",
        type: "#f9e2af",
        class: "#f9e2af",
        namespace: "#f9e2af",
        label: "#74c7ec",
        macro: "#f5e0dc",
        atom: "#fab387",
        bool: "#fab387",
        punctuation: "#9399b2",
        meta: "#fab387",
        link: "#89b4fa",
        heading: "#89b4fa",
        invalid: "#f38ba8",
        deleted: "#f38ba8",
        inserted: "#a6e3a1",
      },
      codeForeground: "#cdd6f4",
      codeBackground: "#1e1e2e",
    },
    source: "https://catppuccin.com/palette/",
  },
  {
    id: "nord",
    label: "Nord",
    mode: "dark",
    dark: {
      bgPrimary: "#2e3440",
      bgSecondary: "#2e3440",
      bgHover: "#2e3440",
      textPrimary: "#d8dee9",
      textSecondary: "#c7cdd8",
      borderColor: "#3b4252",
      accent: "#88c0d0",
      accentHover: "#88c0d0",
      danger: "#bf616a",
      success: "#a3be8c",
      warning: "#ebcb8b",
      syntax: {
        keyword: "#81a1c1",
        operator: "#81a1c1",
        number: "#b48ead",
        string: "#a3be8c",
        regexp: "#ebcb8b",
        comment: "#616e88",
        variable: "#d8dee9",
        variable2: "#81a1c1",
        definition: "#d8dee9",
        function: "#88c0d0",
        local: "#d8dee9",
        property: "#8fbcbb",
        propertyDefinition: "#8fbcbb",
        type: "#8fbcbb",
        class: "#8fbcbb",
        namespace: "#8fbcbb",
        label: "#d8dee9",
        macro: "#88c0d0",
        atom: "#81a1c1",
        bool: "#81a1c1",
        punctuation: "#eceff4",
        meta: "#5e81ac",
        link: "#a3be8c",
        heading: "#88c0d0",
        invalid: "#d8dee9",
        deleted: "#bf616a",
        inserted: "#a3be8c",
      },
      terminal: {
        black: "#3b4252",
        "bright-black": "#4c566a",
        red: "#bf616a",
        "bright-red": "#bf616a",
        green: "#a3be8c",
        "bright-green": "#a3be8c",
        yellow: "#ebcb8b",
        "bright-yellow": "#ebcb8b",
        blue: "#81a1c1",
        "bright-blue": "#81a1c1",
        magenta: "#b48ead",
        "bright-magenta": "#b48ead",
        cyan: "#88c0d0",
        "bright-cyan": "#8fbcbb",
        white: "#e5e9f0",
        "bright-white": "#eceff4",
      },
      codeForeground: "#d8dee9",
      codeBackground: "#2e3440",
    },
    source: "https://github.com/nordtheme/visual-studio-code",
  },
  {
    id: "gruvbox",
    label: "Gruvbox",
    mode: "dark",
    dark: {
      bgPrimary: "#282828",
      bgSecondary: "#32302f",
      bgHover: "#3c3836",
      textPrimary: "#ebdbb2",
      textSecondary: "#bdae93",
      borderColor: "#3c3836",
      accent: "#d79921",
      accentHover: "#c28515",
      danger: "#fb4934",
      success: "#b8bb26",
      syntax: {
        keyword: "#fb4934",
        operator: "#fb4934",
        number: "#d3869b",
        string: "#b8bb26",
        regexp: "#b8bb26",
        comment: "#928374",
        variable: "#ebdbb2",
        variable2: "#ebdbb2",
        definition: "#ebdbb2",
        function: "#fabd2f",
        local: "#ebdbb2",
        property: "#83a598",
        propertyDefinition: "#83a598",
        type: "#8ec07c",
        class: "#8ec07c",
        namespace: "#8ec07c",
        label: "#ebdbb2",
        macro: "#fabd2f",
        atom: "#d3869b",
        bool: "#d3869b",
        punctuation: "#ebdbb2",
        meta: "#fb4934",
        link: "#b8bb26",
        heading: "#fb4934",
        invalid: "#ebdbb2",
        deleted: "#ebdbb2",
        inserted: "#ebdbb2",
      },
      codeForeground: "#ebdbb2",
      codeBackground: "#282828",
    },
  },
  {
    id: "solarized",
    label: "Solarized",
    mode: "auto",
    light: {
      bgPrimary: "#fdf6e3",
      bgSecondary: "#f5efdc",
      bgHover: "#eee8d5",
      textPrimary: "#586e75",
      textSecondary: "#657b83",
      borderColor: "#e0d8c6",
      accent: "#268bd2",
      accentHover: "#1f78b3",
      danger: "#dc322f",
      success: "#859900",
      codeForeground: "#657b83",
      codeBackground: "#fdf6e3",
      syntax: {
        keyword: "#859900",
        operator: "#6c71c4",
        number: "#d33682",
        string: "#2aa198",
        regexp: "#2aa198",
        comment: "#93a1a1",
        variable: "#657b83",
        variable2: "#657b83",
        definition: "#657b83",
        function: "#268bd2",
        local: "#657b83",
        property: "#268bd2",
        propertyDefinition: "#268bd2",
        type: "#b58900",
        class: "#b58900",
        namespace: "#b58900",
        label: "#657b83",
        macro: "#268bd2",
        atom: "#cb4b16",
        bool: "#cb4b16",
        punctuation: "#657b83",
        meta: "#859900",
        link: "#2aa198",
        heading: "#859900",
        invalid: "#657b83",
        deleted: "#657b83",
        inserted: "#657b83",
      },
    },
    dark: {
      bgPrimary: "#002b36",
      bgSecondary: "#073642",
      bgHover: "#0b3c4a",
      textPrimary: "#eee8d5",
      textSecondary: "#93a1a1",
      borderColor: "#18424a",
      accent: "#268bd2",
      accentHover: "#1f78b3",
      danger: "#dc322f",
      success: "#859900",
      codeForeground: "#839496",
      codeBackground: "#002b36",
      syntax: {
        keyword: "#859900",
        operator: "#6c71c4",
        number: "#d33682",
        string: "#2aa198",
        regexp: "#2aa198",
        comment: "#586e75",
        variable: "#839496",
        variable2: "#839496",
        definition: "#839496",
        function: "#268bd2",
        local: "#839496",
        property: "#268bd2",
        propertyDefinition: "#268bd2",
        type: "#b58900",
        class: "#b58900",
        namespace: "#b58900",
        label: "#839496",
        macro: "#268bd2",
        atom: "#cb4b16",
        bool: "#cb4b16",
        punctuation: "#839496",
        meta: "#859900",
        link: "#2aa198",
        heading: "#859900",
        invalid: "#839496",
        deleted: "#839496",
        inserted: "#839496",
      },
    },
  },
  {
    id: "tokyo",
    label: "Tokyo Night",
    mode: "dark",
    dark: {
      bgPrimary: "#1a1b26",
      bgSecondary: "#16161e",
      bgHover: "#16161e",
      textPrimary: "#a9b1d6",
      textSecondary: "#515670",
      borderColor: "#101014",
      accent: "#7aa2f7",
      accentHover: "#7aa2f7",
      danger: "#f7768e",
      success: "#73daca",
      warning: "#e0af68",
      syntax: {
        keyword: "#bb9af7",
        operator: "#89ddff",
        number: "#ff9e64",
        string: "#9ece6a",
        regexp: "#b4f9f8",
        comment: "#51597d",
        variable: "#c0caf5",
        variable2: "#2ac3de",
        definition: "#bb9af7",
        function: "#7aa2f7",
        local: "#d9d4cd",
        property: "#7dcfff",
        propertyDefinition: "#73daca",
        type: "#c0caf5",
        class: "#c0caf5",
        namespace: "#0db9d7",
        label: "#c0caf5",
        macro: "#7aa2f7",
        atom: "#ff9e64",
        bool: "#ff9e64",
        punctuation: "#89ddff",
        meta: "#73daca",
        link: "#73daca",
        heading: "#bb9af7",
        invalid: "#ff5370",
        deleted: "#914c54",
        inserted: "#449dab",
      },
      terminal: {
        black: "#363b54",
        "bright-black": "#363b54",
        red: "#f7768e",
        "bright-red": "#f7768e",
        green: "#73daca",
        "bright-green": "#73daca",
        yellow: "#e0af68",
        "bright-yellow": "#e0af68",
        blue: "#7aa2f7",
        "bright-blue": "#7aa2f7",
        magenta: "#bb9af7",
        "bright-magenta": "#bb9af7",
        cyan: "#7dcfff",
        "bright-cyan": "#7dcfff",
        white: "#787c99",
        "bright-white": "#acb0d0",
      },
      codeForeground: "#a9b1d6",
      codeBackground: "#1a1b26",
    },
    source: "https://github.com/tokyo-night/tokyo-night-vscode-theme",
  },
  {
    id: "miasma",
    label: "Miasma",
    mode: "dark",
    dark: {
      bgPrimary: "#1f1f23",
      bgSecondary: "#29292f",
      bgHover: "#33333a",
      textPrimary: "#e5e5e5",
      textSecondary: "#b4b4b4",
      borderColor: "#3d3d45",
      accent: "#c9739c",
      accentHover: "#b8618c",
      danger: "#e06c75",
      success: "#98c379",
      codeForeground: "#e5e5e5",
      codeBackground: "#1f1f23",
      syntax: {
        keyword: "#c9739c",
        operator: "#e5e5e5",
        number: "#c9739c",
        string: "#98c379",
        regexp: "#98c379",
        comment: "#b4b4b4",
        variable: "#e5e5e5",
        variable2: "#e5e5e5",
        definition: "#e5e5e5",
        function: "#c9739c",
        local: "#e5e5e5",
        property: "#c9739c",
        propertyDefinition: "#c9739c",
        type: "#e5e5e5",
        class: "#e5e5e5",
        namespace: "#e5e5e5",
        label: "#e5e5e5",
        macro: "#c9739c",
        atom: "#c9739c",
        bool: "#c9739c",
        punctuation: "#e5e5e5",
        meta: "#c9739c",
        link: "#98c379",
        heading: "#c9739c",
        invalid: "#e06c75",
        deleted: "#e06c75",
        inserted: "#98c379",
      },
    },
  },
  {
    id: "github",
    label: "GitHub",
    mode: "auto",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f6f8fa",
      bgHover: "#eaeef2",
      textPrimary: "#24292f",
      textSecondary: "#57606a",
      borderColor: "#d0d7de",
      accent: "#0969da",
      accentHover: "#0550ae",
      danger: "#cf222e",
      success: "#1a7f37",
      codeForeground: "#24292f",
      codeBackground: "#ffffff",
      syntax: {
        keyword: "#cf222e",
        operator: "#cf222e",
        number: "#0550ae",
        string: "#0a3069",
        regexp: "#0a3069",
        comment: "#6e7781",
        variable: "#e16209",
        variable2: "#e16209",
        definition: "#e16209",
        function: "#8250df",
        local: "#e16209",
        property: "#0550ae",
        propertyDefinition: "#0550ae",
        type: "#e16209",
        class: "#e16209",
        namespace: "#e16209",
        label: "#e16209",
        macro: "#8250df",
        atom: "#0550ae",
        bool: "#0550ae",
        punctuation: "#24292f",
        meta: "#cf222e",
        link: "#0a3069",
        heading: "#cf222e",
        invalid: "#24292f",
        deleted: "#24292f",
        inserted: "#24292f",
      },
    },
    dark: {
      bgPrimary: "#0d1117",
      bgSecondary: "#161b22",
      bgHover: "#21262d",
      textPrimary: "#c9d1d9",
      textSecondary: "#8b949e",
      borderColor: "#30363d",
      accent: "#2f81f7",
      accentHover: "#1f6feb",
      danger: "#f85149",
      success: "#3fb950",
      codeForeground: "#e6edf3",
      codeBackground: "#0d1117",
      syntax: {
        keyword: "#ff7b72",
        operator: "#ff7b72",
        number: "#79c0ff",
        string: "#a5d6ff",
        regexp: "#a5d6ff",
        comment: "#8b949e",
        variable: "#ffa657",
        variable2: "#ffa657",
        definition: "#ffa657",
        function: "#d2a8ff",
        local: "#ffa657",
        property: "#79c0ff",
        propertyDefinition: "#79c0ff",
        type: "#ffa657",
        class: "#ffa657",
        namespace: "#ffa657",
        label: "#ffa657",
        macro: "#d2a8ff",
        atom: "#79c0ff",
        bool: "#79c0ff",
        punctuation: "#e6edf3",
        meta: "#ff7b72",
        link: "#a5d6ff",
        heading: "#ff7b72",
        invalid: "#e6edf3",
        deleted: "#e6edf3",
        inserted: "#e6edf3",
      },
    },
  },
  {
    id: "gotham",
    label: "Gotham",
    mode: "dark",
    dark: {
      bgPrimary: "#0b0f14",
      bgSecondary: "#111720",
      bgHover: "#18212b",
      textPrimary: "#cbd6e2",
      textSecondary: "#9bb0c3",
      borderColor: "#1f2a37",
      accent: "#5ccfe6",
      accentHover: "#48b8ce",
      danger: "#d26937",
      success: "#2aa889",
      codeForeground: "#cbd6e2",
      codeBackground: "#0b0f14",
      syntax: {
        keyword: "#5ccfe6",
        operator: "#cbd6e2",
        number: "#5ccfe6",
        string: "#2aa889",
        regexp: "#2aa889",
        comment: "#9bb0c3",
        variable: "#cbd6e2",
        variable2: "#cbd6e2",
        definition: "#cbd6e2",
        function: "#5ccfe6",
        local: "#cbd6e2",
        property: "#5ccfe6",
        propertyDefinition: "#5ccfe6",
        type: "#cbd6e2",
        class: "#cbd6e2",
        namespace: "#cbd6e2",
        label: "#cbd6e2",
        macro: "#5ccfe6",
        atom: "#5ccfe6",
        bool: "#5ccfe6",
        punctuation: "#cbd6e2",
        meta: "#5ccfe6",
        link: "#2aa889",
        heading: "#5ccfe6",
        invalid: "#d26937",
        deleted: "#d26937",
        inserted: "#2aa889",
      },
    },
  },
  {
    id: "one-dark-pro",
    label: "One Dark Pro",
    mode: "dark",
    dark: {
      bgPrimary: "#282c34",
      bgSecondary: "#21252b",
      bgHover: "#3e4451",
      textPrimary: "#abb2bf",
      textSecondary: "#5c6370",
      borderColor: "#3e4451",
      accent: "#61afef",
      accentHover: "#61afef",
      danger: "#e06c75",
      success: "#98c379",
      warning: "#e5c07b",
      syntax: {
        keyword: "#c678dd",
        operator: "#56b6c2",
        number: "#d19a66",
        string: "#98c379",
        regexp: "#98c379",
        comment: "#5c6370",
        variable: "#e06c75",
        variable2: "#e06c75",
        definition: "#e06c75",
        function: "#61afef",
        local: "#e06c75",
        property: "#e06c75",
        propertyDefinition: "#e06c75",
        type: "#e5c07b",
        class: "#e5c07b",
        namespace: "#e5c07b",
        label: "#e06c75",
        macro: "#61afef",
        atom: "#d19a66",
        bool: "#d19a66",
        punctuation: "#abb2bf",
        meta: "#c678dd",
        link: "#98c379",
        heading: "#c678dd",
        invalid: "#abb2bf",
        deleted: "#abb2bf",
        inserted: "#abb2bf",
      },
      codeForeground: "#abb2bf",
      codeBackground: "#282c34",
    },
  },
  {
    id: "solarized-dark",
    label: "Solarized Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#002b36",
      bgSecondary: "#002028",
      bgHover: "#073642",
      textPrimary: "#839496",
      textSecondary: "#586e75",
      borderColor: "#073642",
      accent: "#268bd2",
      accentHover: "#268bd2",
      danger: "#dc322f",
      success: "#859900",
      warning: "#b58900",
      syntax: {
        keyword: "#859900",
        operator: "#6c71c4",
        number: "#d33682",
        string: "#2aa198",
        regexp: "#2aa198",
        comment: "#586e75",
        variable: "#839496",
        variable2: "#839496",
        definition: "#839496",
        function: "#268bd2",
        local: "#839496",
        property: "#268bd2",
        propertyDefinition: "#268bd2",
        type: "#b58900",
        class: "#b58900",
        namespace: "#b58900",
        label: "#839496",
        macro: "#268bd2",
        atom: "#cb4b16",
        bool: "#cb4b16",
        punctuation: "#839496",
        meta: "#859900",
        link: "#2aa198",
        heading: "#859900",
        invalid: "#839496",
        deleted: "#839496",
        inserted: "#839496",
      },
      codeForeground: "#839496",
      codeBackground: "#002b36",
    },
  },
  {
    id: "solarized-light",
    label: "Solarized Light",
    mode: "light",
    light: {
      bgPrimary: "#fdf6e3",
      bgSecondary: "#eee8d5",
      bgHover: "#eee8d5",
      textPrimary: "#657b83",
      textSecondary: "#93a1a1",
      borderColor: "#d2cbb8",
      accent: "#268bd2",
      accentHover: "#268bd2",
      danger: "#dc322f",
      success: "#859900",
      warning: "#b58900",
      syntax: {
        keyword: "#859900",
        operator: "#6c71c4",
        number: "#d33682",
        string: "#2aa198",
        regexp: "#2aa198",
        comment: "#93a1a1",
        variable: "#657b83",
        variable2: "#657b83",
        definition: "#657b83",
        function: "#268bd2",
        local: "#657b83",
        property: "#268bd2",
        propertyDefinition: "#268bd2",
        type: "#b58900",
        class: "#b58900",
        namespace: "#b58900",
        label: "#657b83",
        macro: "#268bd2",
        atom: "#cb4b16",
        bool: "#cb4b16",
        punctuation: "#657b83",
        meta: "#859900",
        link: "#2aa198",
        heading: "#859900",
        invalid: "#657b83",
        deleted: "#657b83",
        inserted: "#657b83",
      },
      codeForeground: "#657b83",
      codeBackground: "#fdf6e3",
    },
  },
  {
    id: "github-dark",
    label: "GitHub Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#0d1117",
      bgSecondary: "#010409",
      bgHover: "#161b22",
      textPrimary: "#e6edf3",
      textSecondary: "#6e7681",
      borderColor: "#30363d",
      accent: "#58a6ff",
      accentHover: "#58a6ff",
      danger: "#f85149",
      success: "#3fb950",
      warning: "#d29922",
      syntax: {
        keyword: "#ff7b72",
        operator: "#ff7b72",
        number: "#79c0ff",
        string: "#a5d6ff",
        regexp: "#a5d6ff",
        comment: "#8b949e",
        variable: "#ffa657",
        variable2: "#ffa657",
        definition: "#ffa657",
        function: "#d2a8ff",
        local: "#ffa657",
        property: "#79c0ff",
        propertyDefinition: "#79c0ff",
        type: "#ffa657",
        class: "#ffa657",
        namespace: "#ffa657",
        label: "#ffa657",
        macro: "#d2a8ff",
        atom: "#79c0ff",
        bool: "#79c0ff",
        punctuation: "#e6edf3",
        meta: "#ff7b72",
        link: "#a5d6ff",
        heading: "#ff7b72",
        invalid: "#e6edf3",
        deleted: "#e6edf3",
        inserted: "#e6edf3",
      },
      codeForeground: "#e6edf3",
      codeBackground: "#0d1117",
    },
  },
  {
    id: "vscode-dark",
    label: "VS Code Dark Modern",
    mode: "dark",
    dark: {
      bgPrimary: "#1f1f1f",
      bgSecondary: "#181818",
      bgHover: "#2d2d2d",
      textPrimary: "#d4d4d4",
      textSecondary: "#858585",
      borderColor: "#3c3c3c",
      accent: "#0078d4",
      accentHover: "#0078d4",
      danger: "#f44747",
      success: "#4ec9b0",
      warning: "#ce9178",
      syntax: {
        keyword: "#569cd6",
        operator: "#d4d4d4",
        number: "#b5cea8",
        string: "#ce9178",
        regexp: "#ce9178",
        comment: "#6a9955",
        variable: "#9cdcfe",
        variable2: "#9cdcfe",
        definition: "#9cdcfe",
        function: "#dcdcaa",
        local: "#9cdcfe",
        property: "#9cdcfe",
        propertyDefinition: "#9cdcfe",
        type: "#4ec9b0",
        class: "#4ec9b0",
        namespace: "#4ec9b0",
        label: "#9cdcfe",
        macro: "#dcdcaa",
        atom: "#b5cea8",
        bool: "#b5cea8",
        punctuation: "#d4d4d4",
        meta: "#569cd6",
        link: "#ce9178",
        heading: "#569cd6",
        invalid: "#d4d4d4",
        deleted: "#d4d4d4",
        inserted: "#d4d4d4",
      },
      codeForeground: "#d4d4d4",
      codeBackground: "#1f1f1f",
    },
  },
  {
    id: "vscode-light",
    label: "VS Code Light Modern",
    mode: "light",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f8f8f8",
      bgHover: "#f3f3f3",
      textPrimary: "#3b3b3b",
      textSecondary: "#767676",
      borderColor: "#e0e0e0",
      accent: "#005fb8",
      accentHover: "#005fb8",
      danger: "#e51400",
      success: "#008000",
      warning: "#bf8803",
      syntax: {
        keyword: "#0000ff",
        operator: "#3b3b3b",
        number: "#098658",
        string: "#a31515",
        regexp: "#a31515",
        comment: "#008000",
        variable: "#001080",
        variable2: "#001080",
        definition: "#001080",
        function: "#795e26",
        local: "#001080",
        property: "#001080",
        propertyDefinition: "#001080",
        type: "#267f99",
        class: "#267f99",
        namespace: "#267f99",
        label: "#001080",
        macro: "#795e26",
        atom: "#098658",
        bool: "#098658",
        punctuation: "#3b3b3b",
        meta: "#0000ff",
        link: "#a31515",
        heading: "#0000ff",
        invalid: "#3b3b3b",
        deleted: "#3b3b3b",
        inserted: "#3b3b3b",
      },
      codeForeground: "#3b3b3b",
      codeBackground: "#ffffff",
    },
  },
  {
    id: "github-light",
    label: "GitHub Light",
    mode: "light",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f6f8fa",
      bgHover: "#f6f8fa",
      textPrimary: "#24292f",
      textSecondary: "#6e7781",
      borderColor: "#d0d7de",
      accent: "#0969da",
      accentHover: "#0969da",
      danger: "#cf222e",
      success: "#1a7f37",
      warning: "#9a6700",
      syntax: {
        keyword: "#cf222e",
        operator: "#cf222e",
        number: "#0550ae",
        string: "#0a3069",
        regexp: "#0a3069",
        comment: "#6e7781",
        variable: "#e16209",
        variable2: "#e16209",
        definition: "#e16209",
        function: "#8250df",
        local: "#e16209",
        property: "#0550ae",
        propertyDefinition: "#0550ae",
        type: "#e16209",
        class: "#e16209",
        namespace: "#e16209",
        label: "#e16209",
        macro: "#8250df",
        atom: "#0550ae",
        bool: "#0550ae",
        punctuation: "#24292f",
        meta: "#cf222e",
        link: "#0a3069",
        heading: "#cf222e",
        invalid: "#24292f",
        deleted: "#24292f",
        inserted: "#24292f",
      },
      codeForeground: "#24292f",
      codeBackground: "#ffffff",
    },
  },
  {
    id: "ayu-light",
    label: "Ayu Light",
    mode: "light",
    light: {
      bgPrimary: "#fafafa",
      bgSecondary: "#f3f4f5",
      bgHover: "#f3f4f5",
      textPrimary: "#575f66",
      textSecondary: "#abb0b6",
      borderColor: "#d9d8d7",
      accent: "#399ee6",
      accentHover: "#399ee6",
      danger: "#e65050",
      success: "#86b300",
      warning: "#f29718",
      syntax: {
        keyword: "#fa8d3e",
        operator: "#ed9366",
        number: "#a37acc",
        string: "#86b300",
        regexp: "#86b300",
        comment: "#abb0b6",
        variable: "#575f66",
        variable2: "#575f66",
        definition: "#575f66",
        function: "#399ee6",
        local: "#575f66",
        property: "#4cbf99",
        propertyDefinition: "#4cbf99",
        type: "#399ee6",
        class: "#399ee6",
        namespace: "#399ee6",
        label: "#575f66",
        macro: "#399ee6",
        atom: "#a37acc",
        bool: "#a37acc",
        punctuation: "#575f66",
        meta: "#fa8d3e",
        link: "#86b300",
        heading: "#fa8d3e",
        invalid: "#575f66",
        deleted: "#575f66",
        inserted: "#575f66",
      },
      codeForeground: "#575f66",
      codeBackground: "#fafafa",
    },
  },
  {
    id: "ayu-dark",
    label: "Ayu Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#0d1017",
      bgSecondary: "#0b0e14",
      bgHover: "#131721",
      textPrimary: "#bfbdb6",
      textSecondary: "#3d424d",
      borderColor: "#1a2132",
      accent: "#39bae6",
      accentHover: "#39bae6",
      danger: "#f07178",
      success: "#aad94c",
      warning: "#ffb454",
      syntax: {
        keyword: "#ff8f40",
        operator: "#f29668",
        number: "#d2a6ff",
        string: "#aad94c",
        regexp: "#aad94c",
        comment: "#626a73",
        variable: "#bfbdb6",
        variable2: "#bfbdb6",
        definition: "#bfbdb6",
        function: "#39bae6",
        local: "#bfbdb6",
        property: "#59c2ff",
        propertyDefinition: "#59c2ff",
        type: "#39bae6",
        class: "#39bae6",
        namespace: "#39bae6",
        label: "#bfbdb6",
        macro: "#39bae6",
        atom: "#d2a6ff",
        bool: "#d2a6ff",
        punctuation: "#bfbdb6",
        meta: "#ff8f40",
        link: "#aad94c",
        heading: "#ff8f40",
        invalid: "#bfbdb6",
        deleted: "#bfbdb6",
        inserted: "#bfbdb6",
      },
      codeForeground: "#bfbdb6",
      codeBackground: "#0d1017",
    },
  },
  {
    id: "gruvbox-light",
    label: "Gruvbox Light",
    mode: "light",
    light: {
      bgPrimary: "#fbf1c7",
      bgSecondary: "#f2e5bc",
      bgHover: "#ebdbb2",
      textPrimary: "#3c3836",
      textSecondary: "#928374",
      borderColor: "#d5c4a1",
      accent: "#d79921",
      accentHover: "#d79921",
      danger: "#9d0006",
      success: "#79740e",
      warning: "#b57614",
      syntax: {
        keyword: "#9d0006",
        operator: "#9d0006",
        number: "#8f3f71",
        string: "#79740e",
        regexp: "#79740e",
        comment: "#928374",
        variable: "#3c3836",
        variable2: "#3c3836",
        definition: "#3c3836",
        function: "#b57614",
        local: "#3c3836",
        property: "#076678",
        propertyDefinition: "#076678",
        type: "#427b58",
        class: "#427b58",
        namespace: "#427b58",
        label: "#3c3836",
        macro: "#b57614",
        atom: "#8f3f71",
        bool: "#8f3f71",
        punctuation: "#3c3836",
        meta: "#9d0006",
        link: "#79740e",
        heading: "#9d0006",
        invalid: "#3c3836",
        deleted: "#3c3836",
        inserted: "#3c3836",
      },
      codeForeground: "#3c3836",
      codeBackground: "#fbf1c7",
    },
  },
  {
    id: "rose-pine",
    label: "Rosé Pine",
    mode: "dark",
    dark: {
      bgPrimary: "#191724",
      bgSecondary: "#1f1d2e",
      bgHover: "#26233a",
      textPrimary: "#e0def4",
      textSecondary: "#8d89a4",
      borderColor: "#908caa",
      accent: "#c4a7e7",
      accentHover: "#c4a7e7",
      danger: "#eb6f92",
      success: "#9ccfd8",
      warning: "#f6c177",
      syntax: {
        keyword: "#31748f",
        operator: "#908caa",
        number: "#f6c177",
        string: "#9ccfd8",
        regexp: "#c4a7e7",
        comment: "#6e6a86",
        variable: "#e0def4",
        variable2: "#eb6f92",
        definition: "#e0def4",
        function: "#ebbcba",
        local: "#e0def4",
        property: "#9ccfd8",
        propertyDefinition: "#9ccfd8",
        type: "#c4a7e7",
        class: "#c4a7e7",
        namespace: "#c4a7e7",
        label: "#f6c177",
        macro: "#ebbcba",
        atom: "#f6c177",
        bool: "#f6c177",
        punctuation: "#908caa",
        meta: "#c4a7e7",
        link: "#c4a7e7",
        heading: "#ebbcba",
        invalid: "#eb6f92",
        deleted: "#eb6f92",
        inserted: "#9ccfd8",
      },
      codeForeground: "#e0def4",
      codeBackground: "#191724",
    },
    source: "https://rosepinetheme.com/palette/",
  },
  {
    id: "catppuccin-latte",
    label: "Catppuccin Latte",
    mode: "light",
    light: {
      bgPrimary: "#eff1f5",
      bgSecondary: "#e6e9ef",
      bgHover: "#ccd0da",
      textPrimary: "#4c4f69",
      textSecondary: "#555871",
      borderColor: "#acb0be",
      accent: "#1e66f5",
      accentHover: "#1e66f5",
      danger: "#d20f39",
      success: "#40a02b",
      warning: "#df8e1d",
      syntax: {
        keyword: "#8839ef",
        operator: "#04a5e5",
        number: "#fe640b",
        string: "#40a02b",
        regexp: "#ea76cb",
        comment: "#7c7f93",
        variable: "#4c4f69",
        variable2: "#d20f39",
        definition: "#4c4f69",
        function: "#1e66f5",
        local: "#e64553",
        property: "#1e66f5",
        propertyDefinition: "#1e66f5",
        type: "#df8e1d",
        class: "#df8e1d",
        namespace: "#df8e1d",
        label: "#209fb5",
        macro: "#dc8a78",
        atom: "#fe640b",
        bool: "#fe640b",
        punctuation: "#7c7f93",
        meta: "#fe640b",
        link: "#1e66f5",
        heading: "#1e66f5",
        invalid: "#d20f39",
        deleted: "#d20f39",
        inserted: "#40a02b",
      },
      codeForeground: "#4c4f69",
      codeBackground: "#eff1f5",
    },
    source: "https://catppuccin.com/palette/",
  },
  {
    id: "everforest-dark",
    label: "Everforest Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#2d353b",
      bgSecondary: "#343f44",
      bgHover: "#3d484d",
      textPrimary: "#d3c6aa",
      textSecondary: "#b3b5a4",
      borderColor: "#66766b",
      accent: "#a7c080",
      accentHover: "#a7c080",
      danger: "#e67e80",
      success: "#a7c080",
      warning: "#dbbc7f",
      syntax: {
        keyword: "#e69875",
        operator: "#d3c6aa",
        number: "#d699b6",
        string: "#a7c080",
        regexp: "#a7c080",
        comment: "#b3b5a4",
        variable: "#d3c6aa",
        variable2: "#d3c6aa",
        definition: "#d3c6aa",
        function: "#a7c080",
        local: "#d3c6aa",
        property: "#a7c080",
        propertyDefinition: "#a7c080",
        type: "#d3c6aa",
        class: "#d3c6aa",
        namespace: "#d3c6aa",
        label: "#d3c6aa",
        macro: "#a7c080",
        atom: "#d699b6",
        bool: "#d699b6",
        punctuation: "#d3c6aa",
        meta: "#e69875",
        link: "#a7c080",
        heading: "#e69875",
        invalid: "#e67e80",
        deleted: "#e67e80",
        inserted: "#a7c080",
      },
      codeForeground: "#d3c6aa",
      codeBackground: "#2d353b",
    },
    source: "https://github.com/sainnhe/everforest",
  },
  {
    id: "everforest-light",
    label: "Everforest Light",
    mode: "light",
    light: {
      bgPrimary: "#fdf6e3",
      bgSecondary: "#f4f0d9",
      bgHover: "#efebd4",
      textPrimary: "#5c6a72",
      textSecondary: "#5e6c73",
      borderColor: "#a6b0a0",
      accent: "#8da101",
      accentHover: "#8da101",
      danger: "#f85552",
      success: "#8da101",
      warning: "#dfa000",
      syntax: {
        keyword: "#f57d26",
        operator: "#5c6a72",
        number: "#df69ba",
        string: "#8da101",
        regexp: "#8da101",
        comment: "#5e6c73",
        variable: "#5c6a72",
        variable2: "#5c6a72",
        definition: "#5c6a72",
        function: "#8da101",
        local: "#5c6a72",
        property: "#8da101",
        propertyDefinition: "#8da101",
        type: "#5c6a72",
        class: "#5c6a72",
        namespace: "#5c6a72",
        label: "#5c6a72",
        macro: "#8da101",
        atom: "#df69ba",
        bool: "#df69ba",
        punctuation: "#5c6a72",
        meta: "#f57d26",
        link: "#8da101",
        heading: "#f57d26",
        invalid: "#f85552",
        deleted: "#f85552",
        inserted: "#8da101",
      },
      codeForeground: "#5c6a72",
      codeBackground: "#fdf6e3",
    },
    source: "https://github.com/sainnhe/everforest",
  },
  {
    id: "rose-pine-dawn",
    label: "Rosé Pine Dawn",
    mode: "light",
    light: {
      bgPrimary: "#faf4ed",
      bgSecondary: "#fffaf3",
      bgHover: "#f2e9e1",
      textPrimary: "#464261",
      textSecondary: "#6c6780",
      borderColor: "#797593",
      accent: "#d7827e",
      accentHover: "#d7827e",
      danger: "#b4637a",
      success: "#56949f",
      warning: "#ea9d34",
      syntax: {
        keyword: "#286983",
        operator: "#797593",
        number: "#ea9d34",
        string: "#56949f",
        regexp: "#907aa9",
        comment: "#9893a5",
        variable: "#464261",
        variable2: "#b4637a",
        definition: "#464261",
        function: "#d7827e",
        local: "#464261",
        property: "#56949f",
        propertyDefinition: "#56949f",
        type: "#907aa9",
        class: "#907aa9",
        namespace: "#907aa9",
        label: "#ea9d34",
        macro: "#d7827e",
        atom: "#ea9d34",
        bool: "#ea9d34",
        punctuation: "#797593",
        meta: "#907aa9",
        link: "#907aa9",
        heading: "#d7827e",
        invalid: "#b4637a",
        deleted: "#b4637a",
        inserted: "#56949f",
      },
      codeForeground: "#464261",
      codeBackground: "#faf4ed",
    },
    source: "https://rosepinetheme.com/palette/",
  },
  {
    id: "graphite",
    label: "Graphite",
    mode: "dark",
    dark: {
      bgPrimary: "#202326",
      bgSecondary: "#292d31",
      bgHover: "#32383d",
      textPrimary: "#e6e8ea",
      textSecondary: "#a6adb4",
      borderColor: "#66717b",
      accent: "#93b9cc",
      accentHover: "#93b9cc",
      danger: "#e3a49e",
      success: "#a7c6b0",
      warning: "#ddca92",
      syntax: {
        keyword: "#93b9cc",
        operator: "#e6e8ea",
        number: "#ddca92",
        string: "#a7c6b0",
        regexp: "#a7c6b0",
        comment: "#a6adb4",
        variable: "#e6e8ea",
        variable2: "#e6e8ea",
        definition: "#e6e8ea",
        function: "#93b9cc",
        local: "#e6e8ea",
        property: "#93b9cc",
        propertyDefinition: "#93b9cc",
        type: "#e6e8ea",
        class: "#e6e8ea",
        namespace: "#e6e8ea",
        label: "#e6e8ea",
        macro: "#93b9cc",
        atom: "#ddca92",
        bool: "#ddca92",
        punctuation: "#e6e8ea",
        meta: "#93b9cc",
        link: "#a7c6b0",
        heading: "#93b9cc",
        invalid: "#e3a49e",
        deleted: "#e3a49e",
        inserted: "#a7c6b0",
      },
      codeForeground: "#e6e8ea",
      codeBackground: "#202326",
    },
  },
  {
    id: "paper",
    label: "Paper",
    mode: "light",
    light: {
      bgPrimary: "#f8f7f3",
      bgSecondary: "#efeee8",
      bgHover: "#e6e4dc",
      textPrimary: "#292e31",
      textSecondary: "#5f676c",
      borderColor: "#a9afb0",
      accent: "#376a7d",
      accentHover: "#376a7d",
      danger: "#a63c39",
      success: "#326947",
      warning: "#896016",
      syntax: {
        keyword: "#376a7d",
        operator: "#292e31",
        number: "#896016",
        string: "#326947",
        regexp: "#326947",
        comment: "#5f676c",
        variable: "#292e31",
        variable2: "#292e31",
        definition: "#292e31",
        function: "#376a7d",
        local: "#292e31",
        property: "#376a7d",
        propertyDefinition: "#376a7d",
        type: "#292e31",
        class: "#292e31",
        namespace: "#292e31",
        label: "#292e31",
        macro: "#376a7d",
        atom: "#896016",
        bool: "#896016",
        punctuation: "#292e31",
        meta: "#376a7d",
        link: "#326947",
        heading: "#376a7d",
        invalid: "#a63c39",
        deleted: "#a63c39",
        inserted: "#326947",
      },
      codeForeground: "#292e31",
      codeBackground: "#f8f7f3",
    },
  },
  {
    id: "accessible-dark",
    label: "Accessible Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#080b0d",
      bgSecondary: "#11171b",
      bgHover: "#1c2429",
      textPrimary: "#ffffff",
      textSecondary: "#dce4e9",
      borderColor: "#adbfc9",
      accent: "#8addff",
      accentHover: "#8addff",
      danger: "#ffb2b2",
      success: "#97e6ac",
      warning: "#ffe48a",
      syntax: {
        keyword: "#8addff",
        operator: "#ffffff",
        number: "#ffe48a",
        string: "#97e6ac",
        regexp: "#97e6ac",
        comment: "#dce4e9",
        variable: "#ffffff",
        variable2: "#ffffff",
        definition: "#ffffff",
        function: "#8addff",
        local: "#ffffff",
        property: "#8addff",
        propertyDefinition: "#8addff",
        type: "#ffffff",
        class: "#ffffff",
        namespace: "#ffffff",
        label: "#ffffff",
        macro: "#8addff",
        atom: "#ffe48a",
        bool: "#ffe48a",
        punctuation: "#ffffff",
        meta: "#8addff",
        link: "#97e6ac",
        heading: "#8addff",
        invalid: "#ffb2b2",
        deleted: "#ffb2b2",
        inserted: "#97e6ac",
      },
      codeForeground: "#ffffff",
      codeBackground: "#080b0d",
    },
  },
  {
    id: "accessible-light",
    label: "Accessible Light",
    mode: "light",
    light: {
      bgPrimary: "#ffffff",
      bgSecondary: "#f3f5f6",
      bgHover: "#e7ecef",
      textPrimary: "#11191f",
      textSecondary: "#35434c",
      borderColor: "#526571",
      accent: "#004b76",
      accentHover: "#004b76",
      danger: "#8c1725",
      success: "#155b2e",
      warning: "#735100",
      syntax: {
        keyword: "#004b76",
        operator: "#11191f",
        number: "#735100",
        string: "#155b2e",
        regexp: "#155b2e",
        comment: "#35434c",
        variable: "#11191f",
        variable2: "#11191f",
        definition: "#11191f",
        function: "#004b76",
        local: "#11191f",
        property: "#004b76",
        propertyDefinition: "#004b76",
        type: "#11191f",
        class: "#11191f",
        namespace: "#11191f",
        label: "#11191f",
        macro: "#004b76",
        atom: "#735100",
        bool: "#735100",
        punctuation: "#11191f",
        meta: "#004b76",
        link: "#155b2e",
        heading: "#004b76",
        invalid: "#8c1725",
        deleted: "#8c1725",
        inserted: "#155b2e",
      },
      codeForeground: "#11191f",
      codeBackground: "#ffffff",
    },
  },
  {
    id: "oled",
    label: "OLED Night",
    mode: "dark",
    dark: {
      bgPrimary: "#000000",
      bgSecondary: "#090c0f",
      bgHover: "#14191d",
      textPrimary: "#f1f5f7",
      textSecondary: "#a8b6be",
      borderColor: "#526773",
      accent: "#6bd8df",
      accentHover: "#6bd8df",
      danger: "#f6a4a6",
      success: "#8bd7a3",
      warning: "#e8cf88",
      syntax: {
        keyword: "#6bd8df",
        operator: "#f1f5f7",
        number: "#e8cf88",
        string: "#8bd7a3",
        regexp: "#8bd7a3",
        comment: "#a8b6be",
        variable: "#f1f5f7",
        variable2: "#f1f5f7",
        definition: "#f1f5f7",
        function: "#6bd8df",
        local: "#f1f5f7",
        property: "#6bd8df",
        propertyDefinition: "#6bd8df",
        type: "#f1f5f7",
        class: "#f1f5f7",
        namespace: "#f1f5f7",
        label: "#f1f5f7",
        macro: "#6bd8df",
        atom: "#e8cf88",
        bool: "#e8cf88",
        punctuation: "#f1f5f7",
        meta: "#6bd8df",
        link: "#8bd7a3",
        heading: "#6bd8df",
        invalid: "#f6a4a6",
        deleted: "#f6a4a6",
        inserted: "#8bd7a3",
      },
      codeForeground: "#f1f5f7",
      codeBackground: "#000000",
    },
  },
  {
    id: "petrol",
    label: "Petrol & Copper",
    mode: "dark",
    dark: {
      bgPrimary: "#102b30",
      bgSecondary: "#18373c",
      bgHover: "#24474b",
      textPrimary: "#f0e8da",
      textSecondary: "#b7c3bb",
      borderColor: "#577b7d",
      accent: "#d99a6c",
      accentHover: "#d99a6c",
      danger: "#f5a7a2",
      success: "#a8c89a",
      warning: "#e7ca87",
      syntax: {
        keyword: "#d99a6c",
        operator: "#f0e8da",
        number: "#e7ca87",
        string: "#a8c89a",
        regexp: "#a8c89a",
        comment: "#b7c3bb",
        variable: "#f0e8da",
        variable2: "#f0e8da",
        definition: "#f0e8da",
        function: "#d99a6c",
        local: "#f0e8da",
        property: "#d99a6c",
        propertyDefinition: "#d99a6c",
        type: "#f0e8da",
        class: "#f0e8da",
        namespace: "#f0e8da",
        label: "#f0e8da",
        macro: "#d99a6c",
        atom: "#e7ca87",
        bool: "#e7ca87",
        punctuation: "#f0e8da",
        meta: "#d99a6c",
        link: "#a8c89a",
        heading: "#d99a6c",
        invalid: "#f5a7a2",
        deleted: "#f5a7a2",
        inserted: "#a8c89a",
      },
      codeForeground: "#f0e8da",
      codeBackground: "#102b30",
    },
  },
  {
    id: "petrol-light",
    label: "Ivory & Petrol",
    mode: "light",
    light: {
      bgPrimary: "#f0e8da",
      bgSecondary: "#e5ddcd",
      bgHover: "#dad3c3",
      textPrimary: "#18363b",
      textSecondary: "#4a605b",
      borderColor: "#9baca1",
      accent: "#89532f",
      accentHover: "#89532f",
      danger: "#a0403e",
      success: "#396c4a",
      warning: "#805e21",
      syntax: {
        keyword: "#246372",
        operator: "#18363b",
        number: "#89532f",
        string: "#3b6c4b",
        regexp: "#3b6c4b",
        comment: "#4a605b",
        variable: "#18363b",
        variable2: "#18363b",
        definition: "#18363b",
        function: "#89532f",
        local: "#18363b",
        property: "#89532f",
        propertyDefinition: "#89532f",
        type: "#18363b",
        class: "#18363b",
        namespace: "#18363b",
        label: "#18363b",
        macro: "#89532f",
        atom: "#89532f",
        bool: "#89532f",
        punctuation: "#18363b",
        meta: "#246372",
        link: "#3b6c4b",
        heading: "#246372",
        invalid: "#a0403e",
        deleted: "#a0403e",
        inserted: "#396c4a",
      },
      codeForeground: "#18363b",
      codeBackground: "#f0e8da",
    },
  },
  {
    id: "aubergine",
    label: "Aubergine & Gold",
    mode: "dark",
    dark: {
      bgPrimary: "#291f30",
      bgSecondary: "#352b3d",
      bgHover: "#42364b",
      textPrimary: "#f4ebdd",
      textSecondary: "#c1b2c0",
      borderColor: "#7e647e",
      accent: "#e2bc68",
      accentHover: "#e2bc68",
      danger: "#f0a3a6",
      success: "#aeca9a",
      warning: "#e2bc68",
      syntax: {
        keyword: "#cfafd9",
        operator: "#f4ebdd",
        number: "#99cbd2",
        string: "#e2bc68",
        regexp: "#e2bc68",
        comment: "#c1b2c0",
        variable: "#f4ebdd",
        variable2: "#f4ebdd",
        definition: "#f4ebdd",
        function: "#e2bc68",
        local: "#f4ebdd",
        property: "#e2bc68",
        propertyDefinition: "#e2bc68",
        type: "#f4ebdd",
        class: "#f4ebdd",
        namespace: "#f4ebdd",
        label: "#f4ebdd",
        macro: "#e2bc68",
        atom: "#99cbd2",
        bool: "#99cbd2",
        punctuation: "#f4ebdd",
        meta: "#cfafd9",
        link: "#e2bc68",
        heading: "#cfafd9",
        invalid: "#f0a3a6",
        deleted: "#f0a3a6",
        inserted: "#aeca9a",
      },
      codeForeground: "#f4ebdd",
      codeBackground: "#291f30",
    },
  },
  {
    id: "cobalt2",
    label: "Cobalt2",
    mode: "dark",
    dark: {
      bgPrimary: "#193549",
      bgSecondary: "#15232d",
      bgHover: "#234e68",
      textPrimary: "#ffffff",
      textSecondary: "#a6c7dc",
      borderColor: "#547d99",
      accent: "#ffc600",
      accentHover: "#ffc600",
      danger: "#ff8c8c",
      success: "#a5ff90",
      warning: "#ffc600",
      syntax: {
        keyword: "#ff9d00",
        operator: "#ff9d00",
        number: "#ff628c",
        string: "#a5ff90",
        regexp: "#a5ff90",
        comment: "#0088ff",
        variable: "#ffffff",
        variable2: "#fb94ff",
        definition: "#ffffff",
        function: "#ffc600",
        local: "#ffffff",
        property: "#9effff",
        propertyDefinition: "#9effff",
        type: "#ff68b8",
        class: "#ff68b8",
        namespace: "#ffc600",
        label: "#ffc600",
        macro: "#ffc600",
        atom: "#ff628c",
        bool: "#ff628c",
        punctuation: "#e1efff",
        meta: "#9effff",
        link: "#a5ff90",
        heading: "#ff9d00",
        invalid: "#f44542",
        deleted: "#ff628c",
        inserted: "#a5ff90",
      },
      codeForeground: "#ffffff",
      codeBackground: "#193549",
    },
    source: "https://github.com/wesbos/cobalt2-vscode",
  },
  {
    id: "burgundy",
    label: "Burgundy & Rose",
    mode: "dark",
    dark: {
      bgPrimary: "#321f29",
      bgSecondary: "#402b34",
      bgHover: "#503743",
      textPrimary: "#f4e7dc",
      textSecondary: "#cdb2bc",
      borderColor: "#8d6c79",
      accent: "#e3a6b1",
      accentHover: "#e3a6b1",
      danger: "#ffbd78",
      success: "#a5ccb1",
      warning: "#ecd68c",
      syntax: {
        keyword: "#e3a6b1",
        operator: "#f4e7dc",
        number: "#ecd68c",
        string: "#a5ccb1",
        regexp: "#a5ccb1",
        comment: "#cdb2bc",
        variable: "#f4e7dc",
        variable2: "#f4e7dc",
        definition: "#f4e7dc",
        function: "#e3a6b1",
        local: "#f4e7dc",
        property: "#e3a6b1",
        propertyDefinition: "#e3a6b1",
        type: "#f4e7dc",
        class: "#f4e7dc",
        namespace: "#f4e7dc",
        label: "#f4e7dc",
        macro: "#e3a6b1",
        atom: "#ecd68c",
        bool: "#ecd68c",
        punctuation: "#f4e7dc",
        meta: "#e3a6b1",
        link: "#a5ccb1",
        heading: "#e3a6b1",
        invalid: "#ffbd78",
        deleted: "#ffbd78",
        inserted: "#a5ccb1",
      },
      codeForeground: "#f4e7dc",
      codeBackground: "#321f29",
    },
  },
  {
    id: "porcelain",
    label: "Porcelain, Indigo & Persimmon",
    mode: "light",
    light: {
      bgPrimary: "#f6f1e7",
      bgSecondary: "#e9e4d8",
      bgHover: "#ded9cc",
      textPrimary: "#304567",
      textSecondary: "#546071",
      borderColor: "#969cac",
      accent: "#b94e28",
      accentHover: "#b94e28",
      danger: "#913449",
      success: "#456c42",
      warning: "#7f6118",
      syntax: {
        keyword: "#304567",
        operator: "#304567",
        number: "#b94e28",
        string: "#476e5f",
        regexp: "#476e5f",
        comment: "#546071",
        variable: "#304567",
        variable2: "#304567",
        definition: "#304567",
        function: "#b94e28",
        local: "#304567",
        property: "#b94e28",
        propertyDefinition: "#b94e28",
        type: "#304567",
        class: "#304567",
        namespace: "#304567",
        label: "#304567",
        macro: "#b94e28",
        atom: "#b94e28",
        bool: "#b94e28",
        punctuation: "#304567",
        meta: "#304567",
        link: "#476e5f",
        heading: "#304567",
        invalid: "#913449",
        deleted: "#913449",
        inserted: "#456c42",
      },
      codeForeground: "#304567",
      codeBackground: "#f6f1e7",
    },
  },
  {
    id: "synthwave-84",
    label: "SynthWave ’84",
    mode: "dark",
    dark: {
      bgPrimary: "#262335",
      bgSecondary: "#241b2f",
      bgHover: "#342b48",
      textPrimary: "#f4f0ff",
      textSecondary: "#b7b0d0",
      borderColor: "#736282",
      accent: "#ff7edb",
      accentHover: "#ff7edb",
      danger: "#ff9c93",
      success: "#72f1b8",
      warning: "#fede5d",
      syntax: {
        keyword: "#fede5d",
        operator: "#fede5d",
        number: "#f97e72",
        string: "#ff8b39",
        regexp: "#f97e72",
        comment: "#848bbd",
        variable: "#ff7edb",
        variable2: "#ff7edb",
        definition: "#ff7edb",
        function: "#36f9f6",
        local: "#ff7edb",
        property: "#ff7edb",
        propertyDefinition: "#ff7edb",
        type: "#fe4450",
        class: "#fe4450",
        namespace: "#fe4450",
        label: "#fe4450",
        macro: "#36f9f6",
        atom: "#f97e72",
        bool: "#f97e72",
        punctuation: "#ffffff",
        meta: "#fede5d",
        link: "#dd5500",
        heading: "#ff7edb",
        invalid: "#ffffff",
        deleted: "#ffffff",
        inserted: "#ffffff",
      },
      codeForeground: "#ffffff",
      codeBackground: "#262335",
    },
    glow: true,
    source: "https://github.com/robb0wen/synthwave-vscode",
  },
  {
    id: "colour-friendly-dark",
    label: "Colour-friendly Dark",
    mode: "dark",
    dark: {
      bgPrimary: "#202326",
      bgSecondary: "#292d31",
      bgHover: "#32383d",
      textPrimary: "#e6e8ea",
      textSecondary: "#a6adb4",
      borderColor: "#66717b",
      accent: "#93b9cc",
      accentHover: "#93b9cc",
      danger: "#f09769",
      success: "#56b4e9",
      warning: "#e6b65a",
      syntax: {
        keyword: "#93b9cc",
        operator: "#e6e8ea",
        number: "#ddca92",
        string: "#a7c6b0",
        regexp: "#a7c6b0",
        comment: "#a6adb4",
        variable: "#e6e8ea",
        variable2: "#e6e8ea",
        definition: "#e6e8ea",
        function: "#93b9cc",
        local: "#e6e8ea",
        property: "#93b9cc",
        propertyDefinition: "#93b9cc",
        type: "#e6e8ea",
        class: "#e6e8ea",
        namespace: "#e6e8ea",
        label: "#e6e8ea",
        macro: "#93b9cc",
        atom: "#ddca92",
        bool: "#ddca92",
        punctuation: "#e6e8ea",
        meta: "#93b9cc",
        link: "#a7c6b0",
        heading: "#93b9cc",
        invalid: "#f09769",
        deleted: "#f09769",
        inserted: "#56b4e9",
      },
      codeForeground: "#e6e8ea",
      codeBackground: "#202326",
    },
  },
  {
    id: "colour-friendly-light",
    label: "Colour-friendly Light",
    mode: "light",
    light: {
      bgPrimary: "#f8f7f3",
      bgSecondary: "#efeee8",
      bgHover: "#e6e4dc",
      textPrimary: "#292e31",
      textSecondary: "#5f676c",
      borderColor: "#a9afb0",
      accent: "#376a7d",
      accentHover: "#376a7d",
      danger: "#b44500",
      success: "#0072b2",
      warning: "#8c5a00",
      syntax: {
        keyword: "#376a7d",
        operator: "#292e31",
        number: "#896016",
        string: "#326947",
        regexp: "#326947",
        comment: "#5f676c",
        variable: "#292e31",
        variable2: "#292e31",
        definition: "#292e31",
        function: "#376a7d",
        local: "#292e31",
        property: "#376a7d",
        propertyDefinition: "#376a7d",
        type: "#292e31",
        class: "#292e31",
        namespace: "#292e31",
        label: "#292e31",
        macro: "#376a7d",
        atom: "#896016",
        bool: "#896016",
        punctuation: "#292e31",
        meta: "#376a7d",
        link: "#326947",
        heading: "#376a7d",
        invalid: "#b44500",
        deleted: "#b44500",
        inserted: "#0072b2",
      },
      codeForeground: "#292e31",
      codeBackground: "#f8f7f3",
    },
  },
  {
    id: "turbo-pascal",
    label: "Turbo Pascal (Original)",
    mode: "dark",
    dark: {
      bgPrimary: "#000088",
      bgSecondary: "#5c5c5c",
      bgHover: "#000066",
      textPrimary: "#aaaaaa",
      textSecondary: "#c0c0c0",
      borderColor: "#606060",
      accent: "#ffff00",
      accentHover: "#ffff00",
      danger: "#aa0000",
      success: "#00aa00",
      warning: "#aaaa00",
      syntax: {
        keyword: "#ffffff",
        operator: "#ffff00",
        number: "#ff00ff",
        string: "#00ffff",
        regexp: "#00ffff",
        comment: "#00ff00",
        variable: "#aaaaaa",
        variable2: "#aaaaaa",
        definition: "#aaaaaa",
        function: "#ffff00",
        local: "#aaaaaa",
        property: "#aaaaaa",
        propertyDefinition: "#aaaaaa",
        type: "#ffff00",
        class: "#ffff00",
        namespace: "#aaaaaa",
        label: "#ffff00",
        macro: "#ff0000",
        atom: "#ff00ff",
        bool: "#ff00ff",
        punctuation: "#aaaaaa",
        meta: "#ffffff",
        link: "#00ffff",
        heading: "#ffffff",
        invalid: "#aaaaaa",
        deleted: "#aaaaaa",
        inserted: "#aaaaaa",
      },
      terminal: {
        black: "#000000",
        "bright-black": "#555555",
        red: "#aa0000",
        "bright-red": "#ff5555",
        green: "#00aa00",
        "bright-green": "#55ff55",
        yellow: "#aaaa00",
        "bright-yellow": "#ffff55",
        blue: "#000088",
        "bright-blue": "#4848dd",
        magenta: "#aa00aa",
        "bright-magenta": "#ff55ff",
        cyan: "#00aaaa",
        "bright-cyan": "#55ffff",
        white: "#aaaaaa",
        "bright-white": "#ffffff",
      },
      codeForeground: "#aaaaaa",
      codeBackground: "#000088",
    },
    source: "https://github.com/jsolarz/Modern-Turbo-UI-Color-Scheme",
  },
  {
    id: "as400",
    label: "AS/400 Green Screen (5250)",
    mode: "dark",
    dark: {
      bgPrimary: "#000000",
      bgSecondary: "#000000",
      bgHover: "#001a00",
      textPrimary: "#00ff00",
      textSecondary: "#009900",
      borderColor: "#003300",
      accent: "#00ff00",
      accentHover: "#00ff00",
      danger: "#00ff00",
      success: "#00cc00",
      warning: "#00dd00",
      monochrome: true,
      syntax: {
        keyword: "#00ff00",
        operator: "#00ff00",
        number: "#00ee00",
        string: "#00dd00",
        regexp: "#00dd00",
        comment: "#007700",
        variable: "#00ff00",
        variable2: "#00ff00",
        definition: "#00cc00",
        function: "#00cc00",
        local: "#00dd00",
        property: "#00cc00",
        propertyDefinition: "#00cc00",
        type: "#00ff00",
        class: "#00ff00",
        namespace: "#00dd00",
        label: "#00ff00",
        macro: "#00cc00",
        atom: "#00ee00",
        bool: "#00ee00",
        punctuation: "#009900",
        meta: "#00ff00",
        link: "#00dd00",
        heading: "#00ff00",
        invalid: "#00ff00",
        deleted: "#00ff00",
        inserted: "#00cc00",
      },
      terminal: {
        black: "#000000",
        red: "#00ff00",
        green: "#00cc00",
        yellow: "#00dd00",
        blue: "#00bb00",
        magenta: "#00ee00",
        cyan: "#00cc00",
        white: "#00dd00",
        "bright-black": "#009900",
        "bright-red": "#00ff00",
        "bright-green": "#00ff00",
        "bright-yellow": "#00ff00",
        "bright-blue": "#00ee00",
        "bright-magenta": "#00ff00",
        "bright-cyan": "#00ee00",
        "bright-white": "#00ff00",
      },
      codeForeground: "#00ff00",
      codeBackground: "#000000",
    },
    source: "https://github.com/jordandiazdiaz/as400-cursor",
  },
  {
    id: "lumon",
    label: "Lumon",
    mode: "dark",
    dark: {
      bgPrimary: "#1b2d40",
      bgSecondary: "#1b2d40",
      bgHover: "#1b2d40",
      textPrimary: "#d6e2ee",
      textSecondary: "#4a6b80",
      borderColor: "#4a6b80",
      accent: "#4d9ed3",
      accentHover: "#4d9ed3",
      danger: "#4d86b0",
      success: "#5e95bc",
      warning: "#6fa4c9",
      syntax: {
        keyword: "#6fb8e3",
        operator: "#d6e2ee",
        number: "#f2fcff",
        string: "#6fb8e3",
        regexp: "#9dcae5",
        comment: "#4a6b80",
        variable: "#4d9ed3",
        variable2: "#f2fcff",
        definition: "#4d9ed3",
        function: "#4d9ed3",
        local: "#4d9ed3",
        property: "#4d9ed3",
        propertyDefinition: "#4d9ed3",
        type: "#9dcae5",
        class: "#b4e4f6",
        namespace: "#b4e4f6",
        label: "#f2fcff",
        macro: "#9dcae5",
        atom: "#f2fcff",
        bool: "#f2fcff",
        punctuation: "#d6e2ee",
        meta: "#4d9ed3",
        link: "#9dcae5",
        heading: "#6fb8e3",
        invalid: "#4d9ed3",
        deleted: "#4d9ed3",
        inserted: "#6fb8e3",
      },
      terminal: {
        black: "#1b2d40",
        "bright-black": "#4a6b80",
        red: "#4d86b0",
        "bright-red": "#73a6cb",
        green: "#5e95bc",
        "bright-green": "#86b7d8",
        yellow: "#6fa4c9",
        "bright-yellow": "#9dcae5",
        blue: "#6fb8e3",
        "bright-blue": "#f2fcff",
        magenta: "#8bc9eb",
        "bright-magenta": "#b1d8ee",
        cyan: "#b4e4f6",
        "bright-cyan": "#d1eef8",
        white: "#d6e2ee",
        "bright-white": "#ffffff",
      },
      codeForeground: "#d6e2ee",
      codeBackground: "#1b2d40",
    },
    source: "https://github.com/OldJobobo/lumon-theme.vscode",
  },
  {
    id: "noctis",
    label: "Noctis",
    mode: "dark",
    dark: {
      bgPrimary: "#052529",
      bgSecondary: "#041d20",
      bgHover: "#073940",
      textPrimary: "#b2cacd",
      textSecondary: "#929ea0",
      borderColor: "#0e6671",
      accent: "#16b0ab",
      accentHover: "#16b0ab",
      danger: "#e66533",
      success: "#49e9a6",
      warning: "#e4b781",
      syntax: {
        keyword: "#df769b",
        operator: "#df769b",
        number: "#7060eb",
        string: "#49e9a6",
        regexp: "#49e9a6",
        comment: "#5b858b",
        variable: "#e4b781",
        variable2: "#e66533",
        definition: "#e4b781",
        function: "#16a3b6",
        local: "#e4b781",
        property: "#e4b781",
        propertyDefinition: "#e4b781",
        type: "#d67e5c",
        class: "#49ace9",
        namespace: "#d67e5c",
        label: "#49d6e9",
        macro: "#16a3b6",
        atom: "#7060eb",
        bool: "#7060eb",
        punctuation: "#b2cacd",
        meta: "#df769b",
        link: "#49d6e9",
        heading: "#df769b",
        invalid: "#e3541c",
        deleted: "#b2cacd",
        inserted: "#b2cacd",
      },
      terminal: {
        black: "#324a4d",
        "bright-black": "#47686c",
        red: "#e66533",
        "bright-red": "#e97749",
        green: "#49e9a6",
        "bright-green": "#60ebb1",
        yellow: "#e4b781",
        "bright-yellow": "#e69533",
        blue: "#49ace9",
        "bright-blue": "#60b6eb",
        magenta: "#df769b",
        "bright-magenta": "#e798b3",
        cyan: "#49d6e9",
        "bright-cyan": "#60dbeb",
        white: "#b2cacd",
        "bright-white": "#c1d4d7",
      },
      codeForeground: "#b2cacd",
      codeBackground: "#052529",
    },
    source: "https://github.com/liviuschera/noctis",
  },
  {
    id: "noctis-lux",
    label: "Noctis Lux",
    mode: "light",
    light: {
      bgPrimary: "#fef8ec",
      bgSecondary: "#f9f1e1",
      bgHover: "#f2edde",
      textPrimary: "#005661",
      textSecondary: "#929ea0",
      borderColor: "#00c6e0",
      accent: "#008080",
      accentHover: "#008080",
      danger: "#e34e1c",
      success: "#00b368",
      warning: "#f49725",
      syntax: {
        keyword: "#ff5792",
        operator: "#ff5792",
        number: "#5842ff",
        string: "#00b368",
        regexp: "#00b368",
        comment: "#8ca6a6",
        variable: "#fa8900",
        variable2: "#e64100",
        definition: "#fa8900",
        function: "#0095a8",
        local: "#fa8900",
        property: "#fa8900",
        propertyDefinition: "#fa8900",
        type: "#b3694d",
        class: "#0094f0",
        namespace: "#b3694d",
        label: "#00bdd6",
        macro: "#0095a8",
        atom: "#5842ff",
        bool: "#5842ff",
        punctuation: "#005661",
        meta: "#ff5792",
        link: "#00bdd6",
        heading: "#ff5792",
        invalid: "#ff530f",
        deleted: "#005661",
        inserted: "#005661",
      },
      terminal: {
        black: "#003b42",
        "bright-black": "#004d57",
        red: "#e34e1c",
        "bright-red": "#ff4000",
        green: "#00b368",
        "bright-green": "#00d17a",
        yellow: "#f49725",
        "bright-yellow": "#ff8c00",
        blue: "#0094f0",
        "bright-blue": "#0fa3ff",
        magenta: "#ff5792",
        "bright-magenta": "#ff6b9f",
        cyan: "#00bdd6",
        "bright-cyan": "#00cbe6",
        white: "#8ca6a6",
        "bright-white": "#bbc3c4",
      },
      codeForeground: "#005661",
      codeBackground: "#fef8ec",
    },
    source: "https://github.com/liviuschera/noctis",
  },
  {
    id: "bearded-arc",
    label: "Bearded Arc",
    mode: "dark",
    dark: {
      bgPrimary: "#1c2433",
      bgSecondary: "#181f2c",
      bgHover: "#253043",
      textPrimary: "#d3d8e1",
      textSecondary: "#d3d8e1",
      borderColor: "#11161f",
      accent: "#8196b5",
      accentHover: "#8196b5",
      danger: "#e35535",
      success: "#3cec85",
      warning: "#eacd61",
      syntax: {
        keyword: "#eacd61",
        operator: "#d3d8e160",
        number: "#ff955c",
        string: "#3cec85",
        regexp: "#3cec85",
        comment: "#54617b",
        variable: "#ff738a",
        variable2: "#22ecdb",
        definition: "#ff738a",
        function: "#69c3ff",
        local: "#f38cec",
        property: "#ff955c",
        propertyDefinition: "#d3d8e1",
        type: "#b78aff",
        class: "#b78aff",
        namespace: "#69c3ff",
        label: "#ff738a",
        macro: "#69c3ff",
        atom: "#b78aff",
        bool: "#e35535",
        punctuation: "#d3d8e160",
        meta: "#eacd61",
        link: "#69c3ff",
        heading: "#eacd61",
        invalid: "#d3d8e1",
        deleted: "#d3d8e1",
        inserted: "#d3d8e1",
      },
      terminal: {
        black: "#1c2433",
        "bright-black": "#69c3ff",
        red: "#e35535",
        "bright-red": "#e35535",
        green: "#3cec85",
        "bright-green": "#3cec85",
        yellow: "#eacd61",
        "bright-yellow": "#eacd61",
        blue: "#69c3ff",
        "bright-blue": "#69c3ff",
        magenta: "#f38cec",
        "bright-magenta": "#f38cec",
        cyan: "#22ecdb",
        "bright-cyan": "#22ecdb",
        white: "#d3d8e1",
        "bright-white": "#d3d8e1",
      },
      codeForeground: "#d3d8e1",
      codeBackground: "#1c2433",
    },
    source: "https://github.com/BeardedBear/bearded-theme",
  },
  {
    id: "tokyo-night-storm",
    label: "Tokyo Night Storm",
    mode: "dark",
    dark: {
      bgPrimary: "#24283b",
      bgSecondary: "#1f2335",
      bgHover: "#1f2335",
      textPrimary: "#a9b1d6",
      textSecondary: "#545c7e",
      borderColor: "#1b1e2e",
      accent: "#7aa2f7",
      accentHover: "#7aa2f7",
      danger: "#f7768e",
      success: "#73daca",
      warning: "#e0af68",
      syntax: {
        keyword: "#bb9af7",
        operator: "#89ddff",
        number: "#ff9e64",
        string: "#9ece6a",
        regexp: "#b4f9f8",
        comment: "#5f6996",
        variable: "#c0caf5",
        variable2: "#2ac3de",
        definition: "#bb9af7",
        function: "#7aa2f7",
        local: "#cfc9c2",
        property: "#7dcfff",
        propertyDefinition: "#73daca",
        type: "#c0caf5",
        class: "#c0caf5",
        namespace: "#2ac3de",
        label: "#c0caf5",
        macro: "#7aa2f7",
        atom: "#ff9e64",
        bool: "#ff9e64",
        punctuation: "#89ddff",
        meta: "#73daca",
        link: "#73daca",
        heading: "#bb9af7",
        invalid: "#ff5370",
        deleted: "#914c54",
        inserted: "#449dab",
      },
      terminal: {
        black: "#414868",
        "bright-black": "#414868",
        red: "#f7768e",
        "bright-red": "#f7768e",
        green: "#73daca",
        "bright-green": "#73daca",
        yellow: "#e0af68",
        "bright-yellow": "#e0af68",
        blue: "#7aa2f7",
        "bright-blue": "#7aa2f7",
        magenta: "#bb9af7",
        "bright-magenta": "#bb9af7",
        cyan: "#7dcfff",
        "bright-cyan": "#7dcfff",
        white: "#8089b3",
        "bright-white": "#a9b1d6",
      },
      codeForeground: "#a9b1d6",
      codeBackground: "#24283b",
    },
    source: "https://github.com/tokyo-night/tokyo-night-vscode-theme",
  },
  {
    id: "tokyo-night-light",
    label: "Tokyo Night Light",
    mode: "light",
    light: {
      bgPrimary: "#e6e7ed",
      bgSecondary: "#d6d8df",
      bgHover: "#d6d8df",
      textPrimary: "#343b59",
      textSecondary: "#707280",
      borderColor: "#c1c2c7",
      accent: "#2959aa",
      accentHover: "#2959aa",
      danger: "#8c4351",
      success: "#33635c",
      warning: "#8f5e15",
      syntax: {
        keyword: "#65359d",
        operator: "#006c86",
        number: "#965027",
        string: "#385f0d",
        regexp: "#3e6968",
        comment: "#888b94",
        variable: "#343b58",
        variable2: "#006c86",
        definition: "#5a3e8e",
        function: "#2959aa",
        local: "#634f30",
        property: "#0f4b6e",
        propertyDefinition: "#33635c",
        type: "#343b58",
        class: "#343b58",
        namespace: "#006c86",
        label: "#343b58",
        macro: "#2959aa",
        atom: "#965027",
        bool: "#965027",
        punctuation: "#006c86",
        meta: "#33635c",
        link: "#33635c",
        heading: "#65359d",
        invalid: "#942f2f",
        deleted: "#914c54",
        inserted: "#449dab",
      },
      terminal: {
        black: "#343b58",
        "bright-black": "#343b58",
        red: "#8c4351",
        "bright-red": "#8c4351",
        green: "#33635c",
        "bright-green": "#33635c",
        yellow: "#8f5e15",
        "bright-yellow": "#8f5e15",
        blue: "#2959aa",
        "bright-blue": "#2959aa",
        magenta: "#7b43ba",
        "bright-magenta": "#7b43ba",
        cyan: "#006c86",
        "bright-cyan": "#006c86",
        white: "#707280",
        "bright-white": "#707280",
      },
      codeForeground: "#343b59",
      codeBackground: "#e6e7ed",
    },
    source: "https://github.com/tokyo-night/tokyo-night-vscode-theme",
  },
  {
    id: "catppuccin-frappe",
    label: "Catppuccin Frappé",
    mode: "dark",
    dark: {
      bgPrimary: "#303446",
      bgSecondary: "#292c3c",
      bgHover: "#414559",
      textPrimary: "#c6d0f5",
      textSecondary: "#a5adce",
      borderColor: "#626880",
      accent: "#8caaee",
      accentHover: "#babbf1",
      danger: "#e78284",
      success: "#a6d189",
      warning: "#e5c890",
      syntax: {
        keyword: "#ca9ee6",
        operator: "#99d1db",
        number: "#ef9f76",
        string: "#a6d189",
        regexp: "#f4b8e4",
        comment: "#949cbb",
        variable: "#c6d0f5",
        variable2: "#e78284",
        definition: "#c6d0f5",
        function: "#8caaee",
        local: "#ea999c",
        property: "#8caaee",
        propertyDefinition: "#8caaee",
        type: "#e5c890",
        class: "#e5c890",
        namespace: "#e5c890",
        label: "#85c1dc",
        macro: "#f2d5cf",
        atom: "#ef9f76",
        bool: "#ef9f76",
        punctuation: "#949cbb",
        meta: "#ef9f76",
        link: "#8caaee",
        heading: "#8caaee",
        invalid: "#e78284",
        deleted: "#e78284",
        inserted: "#a6d189",
      },
      codeForeground: "#c6d0f5",
      codeBackground: "#303446",
    },
    source: "https://github.com/catppuccin/palette",
  },
  {
    id: "catppuccin-macchiato",
    label: "Catppuccin Macchiato",
    mode: "dark",
    dark: {
      bgPrimary: "#24273a",
      bgSecondary: "#1e2030",
      bgHover: "#363a4f",
      textPrimary: "#cad3f5",
      textSecondary: "#a5adcb",
      borderColor: "#5b6078",
      accent: "#8aadf4",
      accentHover: "#b7bdf8",
      danger: "#ed8796",
      success: "#a6da95",
      warning: "#eed49f",
      syntax: {
        keyword: "#c6a0f6",
        operator: "#91d7e3",
        number: "#f5a97f",
        string: "#a6da95",
        regexp: "#f5bde6",
        comment: "#939ab7",
        variable: "#cad3f5",
        variable2: "#ed8796",
        definition: "#cad3f5",
        function: "#8aadf4",
        local: "#ee99a0",
        property: "#8aadf4",
        propertyDefinition: "#8aadf4",
        type: "#eed49f",
        class: "#eed49f",
        namespace: "#eed49f",
        label: "#7dc4e4",
        macro: "#f4dbd6",
        atom: "#f5a97f",
        bool: "#f5a97f",
        punctuation: "#939ab7",
        meta: "#f5a97f",
        link: "#8aadf4",
        heading: "#8aadf4",
        invalid: "#ed8796",
        deleted: "#ed8796",
        inserted: "#a6da95",
      },
      codeForeground: "#cad3f5",
      codeBackground: "#24273a",
    },
    source: "https://github.com/catppuccin/palette",
  },
];
// Full keeps the familiar palette, with a separately selectable neon treatment.
const synthwave = WEB_THEME_PRESETS.find(
  (theme) => theme.id === "synthwave-84",
)!;
WEB_THEME_PRESETS.splice(WEB_THEME_PRESETS.indexOf(synthwave) + 1, 0, {
  ...synthwave,
  id: "synthwave-84-full",
  label: "SynthWave ’84 Full",
});

// Explicit variants and automatic pairs use the same palette data.
for (const family of ["solarized", "github"]) {
  const automatic = WEB_THEME_PRESETS.find((t) => t.id === family)!;
  for (const mode of ["light", "dark"] as const) {
    const variant = WEB_THEME_PRESETS.find((t) => t.id === family + "-" + mode);
    if (variant) variant[mode] = automatic[mode];
  }
}
// Preserve Visual's explicit black Classic bundle, distinct from each skin's adaptive Default.
WEB_THEME_PRESETS.push({
  id: "piclaw-classic",
  label: "PiClaw Classic",
  mode: "dark",
  dark: { ...WEB_THEME_PRESETS[0].dark! },
});
export const WEB_THEME_ALIASES: Record<string, string> = {
  auto: "default",
  drac: "dracula",
  catpp: "catppuccin",
  "catppuccin-mocha": "catppuccin",
  gruv: "gruvbox",
  "gruvbox-dark": "gruvbox",
  "tokyo-night": "tokyo",
  catpuccin: "catppuccin",
  "catpuccin-latte": "catppuccin-latte",
  "as400-green-screen": "as400",
  "as-400": "as400",
  bearded: "bearded-arc",
  turbo: "turbo-pascal",
  "monokai-original": "monokai",
  synthwave: "synthwave-84",
  "synthwave-full": "synthwave-84-full",
  "petrol-copper": "petrol",
  "aubergine-gold": "aubergine",
  "burgundy-rose": "burgundy",
  "ivory-petrol": "petrol-light",
};
export function normaliseWebThemeId(value: unknown): string | null {
  const id = String(value ?? "")
    .trim()
    .toLowerCase();
  const key = WEB_THEME_ALIASES[id] || id;
  return WEB_THEME_PRESETS.some((t) => t.id === key) ? key : null;
}
